/**
 * Cómo se previsualiza cada archivo.
 *
 * La decisión vive acá, compartida, porque el servidor y el panel tienen que
 * estar de acuerdo: si la interfaz ofrece "Ver" para un tipo que la API se
 * niega a servir en línea, el botón abre una pestaña con un error.
 */
export type TipoVista = 'IMAGEN' | 'PDF' | 'OFICINA' | 'HOJA' | 'AUDIO' | 'VIDEO' | 'TEXTO' | 'NINGUNO';

/** Documentos de ofimática: no los dibuja el navegador, hay que convertirlos a PDF. */
const EXTENSIONES_OFICINA: Record<string, TipoVista> = {
  doc: 'OFICINA', docx: 'OFICINA', odt: 'OFICINA', rtf: 'OFICINA',
  ppt: 'OFICINA', pptx: 'OFICINA', odp: 'OFICINA',
  xls: 'HOJA', xlsx: 'HOJA', ods: 'HOJA',
};

/** Texto plano que se puede mostrar tal cual, sin ejecutar nada. */
const EXTENSIONES_TEXTO = new Set([
  'txt', 'md', 'markdown', 'csv', 'tsv', 'log', 'json', 'xml', 'yml', 'yaml', 'sql', 'ini', 'conf',
]);

const MIME_OFICINA: Record<string, TipoVista> = {
  'application/msword': 'OFICINA',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'OFICINA',
  'application/vnd.oasis.opendocument.text': 'OFICINA',
  'application/rtf': 'OFICINA',
  'application/vnd.ms-powerpoint': 'OFICINA',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'OFICINA',
  'application/vnd.oasis.opendocument.presentation': 'OFICINA',
  'application/vnd.ms-excel': 'HOJA',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'HOJA',
  'application/vnd.oasis.opendocument.spreadsheet': 'HOJA',
};

/**
 * Tipo declarado por extensión, para cuando el cliente no dice nada útil.
 *
 * No es una tabla exhaustiva de MIME: son los tipos que la plataforma sabe
 * mostrar. Todo lo demás se queda en `application/octet-stream` y se descarga.
 */
export const MIME_POR_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', avif: 'image/avif', svg: 'image/svg+xml', bmp: 'image/bmp', heic: 'image/heic',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', m4v: 'video/mp4',
  txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', tsv: 'text/tab-separated-values',
  log: 'text/plain', json: 'application/json', xml: 'application/xml',
  yml: 'text/yaml', yaml: 'text/yaml', sql: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text', rtf: 'application/rtf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odp: 'application/vnd.oasis.opendocument.presentation',
  zip: 'application/zip',
};

/**
 * MIME definitivo de un archivo recién subido.
 *
 * Lo que declara el cliente no es confiable: según el navegador y el sistema
 * operativo, un .mp4 o un .xlsx llegan como `application/octet-stream`, y con
 * eso el archivo quedaría sin vista previa para siempre. Se normaliza al
 * entrar, que es el único momento en que se puede arreglar de una vez.
 */
export function normalizarMime(mimeDeclarado: string | null | undefined, nombre: string): string {
  const ext = nombre.split('.').pop()?.toLowerCase() ?? '';
  const porExtension = MIME_POR_EXTENSION[ext];
  const declarado = (mimeDeclarado ?? '').trim().toLowerCase();
  const inutil = !declarado || declarado === 'application/octet-stream' || declarado === 'binary/octet-stream';
  return porExtension ?? (inutil ? 'application/octet-stream' : declarado);
}

/**
 * `extension` manda sobre `mime` para los documentos de ofimática: varios
 * navegadores suben un .docx como `application/octet-stream` y el archivo
 * quedaría marcado como no previsualizable para siempre.
 */
export function tipoDeVista(mime: string | null, nombreOExtension: string | null): TipoVista {
  const ext = (nombreOExtension ?? '').split('.').pop()?.toLowerCase() ?? '';

  if (EXTENSIONES_OFICINA[ext]) return EXTENSIONES_OFICINA[ext]!;
  if (mime && MIME_OFICINA[mime]) return MIME_OFICINA[mime]!;

  if (mime === 'application/pdf' || ext === 'pdf') return 'PDF';
  if (mime?.startsWith('image/')) return 'IMAGEN';
  if (mime?.startsWith('audio/')) return 'AUDIO';
  if (mime?.startsWith('video/')) return 'VIDEO';
  if (mime?.startsWith('text/') || EXTENSIONES_TEXTO.has(ext)) return 'TEXTO';

  // Último recurso para los archivos viejos, subidos antes de que la ingesta
  // normalizara el MIME.
  const porExtension = MIME_POR_EXTENSION[ext];
  if (porExtension?.startsWith('image/')) return 'IMAGEN';
  if (porExtension?.startsWith('audio/')) return 'AUDIO';
  if (porExtension?.startsWith('video/')) return 'VIDEO';

  return 'NINGUNO';
}

/** Los que el navegador dibuja por su cuenta; el resto necesita conversión. */
export const seSirveDirecto = (tipo: TipoVista): boolean =>
  tipo === 'IMAGEN' || tipo === 'PDF' || tipo === 'AUDIO' || tipo === 'VIDEO' || tipo === 'TEXTO';

export const necesitaConversion = (tipo: TipoVista): boolean => tipo === 'OFICINA' || tipo === 'HOJA';

export const ETIQUETA_TIPO_VISTA: Record<TipoVista, string> = {
  IMAGEN: 'Imagen',
  PDF: 'Documento PDF',
  OFICINA: 'Documento de ofimática',
  HOJA: 'Hoja de cálculo',
  AUDIO: 'Audio',
  VIDEO: 'Video',
  TEXTO: 'Texto',
  NINGUNO: 'Archivo',
};
